import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Headphones, Plus, Search, MessageCircle, BookOpen,
  AlertCircle, CheckCircle2, Clock, Send
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const PRIORITY_BADGES = { low: "bg-slate-100 text-slate-600", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };
const STATUS_BADGES = { open: "bg-blue-100 text-blue-700", in_progress: "bg-amber-100 text-amber-700", resolved: "bg-emerald-100 text-emerald-700", closed: "bg-slate-100 text-slate-600" };

const Helpdesk = () => {
  const [tickets, setTickets] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tickets");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [newTicket, setNewTicket] = useState({ subject: "", description: "", category: "general", priority: "medium" });

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const opts = { headers: headers(), withCredentials: true };
      const [tRes, kbRes] = await Promise.all([
        axios.get(`${API}/tickets`, opts),
        axios.get(`${API}/knowledge-base`, opts).catch(() => ({ data: [] })),
      ]);
      setTickets(tRes.data || []);
      setKbArticles(kbRes.data || []);
    } catch { toast.error("Error al cargar datos"); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newTicket.subject) { toast.error("Asunto requerido"); return; }
    try {
      await axios.post(`${API}/tickets`, newTicket, { headers: headers(), withCredentials: true });
      toast.success("Ticket creado");
      setShowCreateDialog(false);
      setNewTicket({ subject: "", description: "", category: "general", priority: "medium" });
      fetchData();
    } catch { toast.error("Error al crear ticket"); }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}/status?status=${status}`, {}, { headers: headers(), withCredentials: true });
      toast.success("Estado actualizado");
      fetchData();
    } catch { toast.error("Error al actualizar"); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API}/tickets/${selectedTicket.ticket_id}/message`, { content: newMessage }, { headers: headers(), withCredentials: true });
      toast.success("Mensaje enviado");
      setNewMessage("");
      fetchData();
    } catch { toast.error("Error al enviar"); }
  };

  const filteredTickets = tickets.filter(t => (t.subject || "").toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return <Layout title="Helpdesk"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;
  }

  return (
    <Layout title="Servicio al Colaborador" subtitle="Tickets de soporte y base de conocimiento"
      actions={
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild><Button data-testid="create-ticket-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo Ticket</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo Ticket</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Asunto</Label><Input value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} data-testid="ticket-subject" /></div>
              <div><Label>Descripción</Label><Textarea rows={3} value={newTicket.description} onChange={e => setNewTicket({ ...newTicket, description: e.target.value })} data-testid="ticket-desc" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Categoría</Label>
                  <Select value={newTicket.category} onValueChange={v => setNewTicket({ ...newTicket, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem><SelectItem value="nomina">Nómina</SelectItem>
                      <SelectItem value="permisos">Permisos</SelectItem><SelectItem value="ti">TI</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Prioridad</Label>
                  <Select value={newTicket.priority} onValueChange={v => setNewTicket({ ...newTicket, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baja</SelectItem><SelectItem value="medium">Media</SelectItem><SelectItem value="high">Alta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="save-ticket-btn">Crear Ticket</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="helpdesk-tabs">
          <TabsTrigger value="tickets"><Headphones className="w-4 h-4 mr-2" /> Tickets</TabsTrigger>
          <TabsTrigger value="kb"><BookOpen className="w-4 h-4 mr-2" /> Base de Conocimiento</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets">
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar tickets..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="tickets-search" />
          </div>
          {filteredTickets.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="tickets-empty">
              <Headphones className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay tickets</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map(t => (
                <Card key={t.ticket_id} className="card-hover cursor-pointer" onClick={() => { setSelectedTicket(t); setShowDetailDialog(true); }} data-testid={`ticket-${t.ticket_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{t.subject}</p>
                      <p className="text-sm text-slate-500 truncate">{t.description}</p>
                    </div>
                    <Badge className={PRIORITY_BADGES[t.priority] || ""}>{t.priority}</Badge>
                    <Badge className={STATUS_BADGES[t.status] || ""}>{t.status}</Badge>
                    <span className="text-xs text-slate-400">{t.created_at?.slice(0, 10)}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kb">
          {kbArticles.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="kb-empty">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay artículos</p>
              <p className="text-sm mt-1">La base de conocimiento se construye con el tiempo</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kbArticles.map(a => (
                <Card key={a.article_id} className="card-hover">
                  <CardContent className="p-4">
                    <p className="font-semibold text-slate-900">{a.title}</p>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{a.content}</p>
                    <Badge variant="outline" className="mt-2 text-xs">{a.category}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selectedTicket?.subject}</DialogTitle></DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">{selectedTicket.description}</p>
              <div className="flex gap-2">
                <Badge className={STATUS_BADGES[selectedTicket.status]}>{selectedTicket.status}</Badge>
                <Badge className={PRIORITY_BADGES[selectedTicket.priority]}>{selectedTicket.priority}</Badge>
              </div>
              <div className="flex gap-2">
                {selectedTicket.status === "open" && <Button size="sm" variant="outline" onClick={() => handleStatusChange(selectedTicket.ticket_id, "in_progress")}>En Progreso</Button>}
                {["open", "in_progress"].includes(selectedTicket.status) && <Button size="sm" onClick={() => handleStatusChange(selectedTicket.ticket_id, "resolved")}>Resolver</Button>}
              </div>
              {(selectedTicket.messages || []).length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedTicket.messages.map((m, i) => (
                    <div key={i} className="p-2 rounded bg-slate-50 text-sm"><p>{m.content}</p><p className="text-xs text-slate-400 mt-1">{m.created_at?.slice(0, 16)}</p></div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input placeholder="Escribir mensaje..." value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendMessage()} />
                <Button size="icon" onClick={handleSendMessage}><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Helpdesk;
